import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  TextField,
  Tooltip,
  Chip,
  Divider,
  InputAdornment,
  Paper,
  Collapse,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import BlockIcon from "@mui/icons-material/Block";

const MAX_RETRIES = 7;
export const DEFAULT_RETRIES = [
  { hours: 3, minutes: 0 },
  { hours: 6, minutes: 0 },
  { hours: 9, minutes: 0 },
];

/**
 * SmartRetrySettings
 *
 * Props:
 *   value   – array of { hours: number, minutes: number }
 *   onChange – (newValue) => void
 *
 * Usage inside RdCampaignStep1:
 *   <SmartRetrySettings
 *     value={campaignPost.retryIntervals ?? DEFAULT_RETRIES}
 *     onChange={(v) => setCampaignPost(p => ({ ...p, retryIntervals: v }))}
 *   />
 *
 * The array maps directly to RETRY_INTERVALS_HOURS on the backend —
 * convert with: retryIntervals.map(r => r.hours + r.minutes / 60)
 */
export default function SmartRetrySettings({ value, onChange }) {
  const retries = value ?? DEFAULT_RETRIES;

  // ── helpers ────────────────────────────────────────────────────────────────

  const totalMinutes = (r) => r.hours * 60 + r.minutes;

  const cumulativeMinutes = useMemo(() => {
    let acc = 0;
    return retries.map((r) => {
      acc += totalMinutes(r);
      return acc;
    });
  }, [retries]);

  const formatDuration = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  // ── mutation helpers ───────────────────────────────────────────────────────

  const updateRetry = (index, field, raw) => {
    const num = Math.max(0, parseInt(raw, 10) || 0);
    const clamped = field === "minutes" ? Math.min(59, num) : Math.min(72, num);
    const next = retries.map((r, i) =>
      i === index ? { ...r, [field]: clamped } : r
    );
    onChange(next);
  };

  const addRetry = () => {
    if (retries.length >= MAX_RETRIES) return;
    const last = retries[retries.length - 1] ?? { hours: 0, minutes: 0 };
    // suggest doubling the last interval, capped at 24h
    const suggestedH = Math.min(24, (last.hours || 1) * 2);
    onChange([...retries, { hours: suggestedH, minutes: 0 }]);
  };

  const removeRetry = (index) => {
    onChange(retries.filter((_, i) => i !== index));
  };

  const resetToDefault = () => onChange([...DEFAULT_RETRIES]);

  // ── validation ─────────────────────────────────────────────────────────────

  const errors = useMemo(() => {
    return retries.map((r, i) => {
      const mins = totalMinutes(r);
      if (mins === 0) return "Interval must be greater than 0";
      if (i > 0 && mins <= totalMinutes(retries[i - 1]))
        return "Must be longer than the previous interval";
      return null;
    });
  }, [retries]);

  const hasErrors = errors.some(Boolean);

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header row */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <ReplayIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            Retry Schedule
          </Typography>
          <Tooltip
            placement="right"
            arrow
            title={
              <Box sx={{ p: 0.5, maxWidth: 260 }}>
                <Typography variant="caption">
                  Each block is the{" "}
                  <strong>wait time after the previous attempt</strong> fails.
                  Retries run in order until all succeed or all attempts are
                  exhausted.
                </Typography>
              </Box>
            }
          >
            <IconButton size="small" sx={{ p: 0 }}>
              <InfoOutlinedIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Button
          size="small"
          variant="text"
          color="inherit"
          onClick={resetToDefault}
          sx={{ fontSize: 12, color: "text.secondary", textTransform: "none" }}
        >
          Reset to defaults
        </Button>
      </Stack>

      {/* Attempt blocks */}
      <Stack spacing={1.5}>
        {retries.map((retry, index) => (
          <RetryBlock
            key={index}
            index={index}
            retry={retry}
            cumulative={cumulativeMinutes[index]}
            error={errors[index]}
            canDelete={retries.length > 1}
            formatDuration={formatDuration}
            onChangeHours={(v) => updateRetry(index, "hours", v)}
            onChangeMinutes={(v) => updateRetry(index, "minutes", v)}
            onDelete={() => removeRetry(index)}
          />
        ))}
      </Stack>

      {/* Add button */}
      {retries.length < MAX_RETRIES && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={addRetry}
          sx={{
            mt: 2,
            borderStyle: "dashed",
            textTransform: "none",
            color: "text.secondary",
            borderColor: "divider",
            "&:hover": { borderColor: "primary.main", color: "primary.main" },
          }}
        >
          Add retry attempt ({retries.length}/{MAX_RETRIES})
        </Button>
      )}

      {retries.length >= MAX_RETRIES && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          Maximum of {MAX_RETRIES} retry attempts reached.
        </Typography>
      )}

      {/* Timeline summary */}
      <Collapse in={!hasErrors && retries.length > 0}>
        <TimelineSummary
          retries={retries}
          cumulativeMinutes={cumulativeMinutes}
          formatDuration={formatDuration}
        />
      </Collapse>
    </Box>
  );
}

// ── RetryBlock ──────────────────────────────────────────────────────────────

function RetryBlock({
  index,
  retry,
  cumulative,
  error,
  canDelete,
  formatDuration,
  onChangeHours,
  onChangeMinutes,
  onDelete,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: error ? "error.light" : "divider",
        backgroundColor: error ? "rgba(211,47,47,0.03)" : "background.paper",
        transition: "border-color 0.2s",
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
        {/* Attempt badge */}
        <Box
          sx={{
            minWidth: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: error ? "error.light" : "primary.main",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            alignSelf: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          {index + 1}
        </Box>

        <Box flex={1}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1}
          >
            <Typography variant="body2" color="text.secondary">
              Attempt {index + 1}
            </Typography>

            {/* Hours input */}
            <TextField
              size="small"
              label="Hours"
              type="number"
              value={retry.hours}
              onChange={(e) => onChangeHours(e.target.value)}
              inputProps={{ min: 0, max: 72, step: 1 }}
              sx={{ width: 120 }}
              error={!!error}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" color="text.secondary">
                      h
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />

            {/* Minutes input */}
            <TextField
              size="small"
              label="Minutes"
              type="number"
              value={retry.minutes}
              onChange={(e) => onChangeMinutes(e.target.value)}
              inputProps={{ min: 0, max: 59, step: 5 }}
              sx={{ width: 110 }}
              error={!!error}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" color="text.secondary">
                      m
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />

            {/* Cumulative chip */}
            {!error && (
              <Chip
                icon={<AccessTimeIcon sx={{ fontSize: "14px !important" }} />}
                label={`${formatDuration(cumulative)} from start`}
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 24, color: "text.secondary" }}
              />
            )}
          </Stack>

          {/* Inline error */}
          {error && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 0.5, display: "block" }}
            >
              {error}
            </Typography>
          )}
        </Box>

        {/* Delete */}
        <Tooltip
          title={
            canDelete ? "Remove this attempt" : "At least one retry required"
          }
          arrow
        >
          <span>
            <IconButton
              size="small"
              onClick={onDelete}
              disabled={!canDelete}
              sx={{
                color: canDelete ? "error.light" : "action.disabled",
                "&:hover": {
                  color: "error.main",
                  backgroundColor: "rgba(211,47,47,0.06)",
                },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

// ── TimelineSummary ─────────────────────────────────────────────────────────

function TimelineSummary({ retries, cumulativeMinutes, formatDuration }) {
  const steps = [
    { label: "Send", icon: "send", color: "primary.main" },
    ...retries.map((_, i) => ({
      label: `Retry ${i + 1}`,
      icon: "retry",
      color: i === retries.length - 1 ? "warning.main" : "primary.main",
      at: cumulativeMinutes[i],
    })),
    { label: "Final", icon: "end", color: "success.main" },
  ];

  return (
    <Box
      sx={{
        mt: 2.5,
        p: 1.5,
        borderRadius: 2,
        backgroundColor: "rgba(0,0,0,0.02)",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        mb={1.5}
        display="block"
      >
        Timeline preview
      </Typography>

      <Box sx={{ overflowX: "auto", pb: 0.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0}
          sx={{ minWidth: "fit-content" }}
        >
          {steps.map((step, i) => (
            <React.Fragment key={i}>
              {/* Node */}
              <Stack alignItems="center" spacing={0.5} sx={{ minWidth: 70 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: "2px solid",
                    borderColor: step.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "background.paper",
                  }}
                >
                  {step.icon === "send" && (
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: step.color,
                      }}
                    />
                  )}
                  {step.icon === "retry" && (
                    <ReplayIcon sx={{ fontSize: 14, color: step.color }} />
                  )}
                  {step.icon === "end" && (
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 16, color: step.color }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 10,
                    color: "text.secondary",
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                >
                  {step.label}
                  {step.at !== undefined && (
                    <>
                      <br />
                      <span style={{ fontWeight: 600 }}>
                        +{formatDuration(step.at)}
                      </span>
                    </>
                  )}
                </Typography>
              </Stack>

              {/* Connector */}
              {i < steps.length - 1 && (
                <Box
                  sx={{
                    flex: 1,
                    height: "2px",
                    backgroundColor: "divider",
                    minWidth: 24,
                    mt: -2.5,
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Stack>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          <strong>{retries.length}</strong> retry attempt
          {retries.length !== 1 ? "s" : ""}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Total window:{" "}
          <strong>
            {formatDuration(cumulativeMinutes[cumulativeMinutes.length - 1])}
          </strong>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Up to <strong>{retries.length + 1}</strong> send attempts per contact
        </Typography>
      </Stack>
    </Box>
  );
}
